import { MAX_LEVEL } from './combat';
import type { EnemyId } from './enemies';

export type ProgressionState = {
  level: number;
  exp: number;
  bie: number;
  spiritEssence: number;
};

export type HauntingLoopState = {
  haunting: number;
  bossActive: boolean;
  cleansingUntilMs: number | null;
  cycle: number;
};

export type EnemyReward = {
  exp: number;
  bie: number;
  spirit: number;
  haunting: number;
};

export type DefeatOutcome = {
  accepted: boolean;
  reward: EnemyReward;
  bossSpawned: boolean;
  bossDefeated: boolean;
  progression: ProgressionState;
  world: HauntingLoopState;
};

export type TickOutcome = {
  cleansingCompleted: boolean;
  progression: ProgressionState;
  world: HauntingLoopState;
};

export const CLEANSING_DELAY_MS = 5000;

export const INITIAL_PROGRESSION: ProgressionState = {
  level: 1,
  exp: 0,
  bie: 0,
  spiritEssence: 0,
};

export const INITIAL_HAUNTING_LOOP: HauntingLoopState = {
  haunting: 0,
  bossActive: false,
  cleansingUntilMs: null,
  cycle: 0,
};

export const ENEMY_REWARDS: Record<EnemyId, EnemyReward> = {
  krahang:{exp:28,bie:18,spirit:0,haunting:17},
  'violent-dead':{exp:32,bie:22,spirit:0,haunting:20},
  pop: { exp: 18, bie: 10, spirit: 0, haunting: 12 },
  krasue: { exp: 20, bie: 12, spirit: 0, haunting: 13 },
  tanee: { exp: 24, bie: 14, spirit: 0, haunting: 16 },
  'mae-tanee': { exp: 180, bie: 180, spirit: 8, haunting: 0 },
};

function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

function cloneProgression(state: ProgressionState): ProgressionState {
  return { ...state };
}

function cloneWorld(state: HauntingLoopState): HauntingLoopState {
  return { ...state };
}

export function expRequiredForLevel(targetLevel: number): number {
  const safeLevel = clampInt(targetLevel, 1, MAX_LEVEL);
  return 100 + (safeLevel - 1) * 50;
}

export function simulateExpGain(
  startLevel: number,
  startExp: number,
  gain: number,
): Pick<ProgressionState, 'level' | 'exp'> {
  let level = clampInt(startLevel, 1, MAX_LEVEL);
  let exp = Math.max(0, Math.trunc(startExp)) + Math.max(0, Math.trunc(gain));

  while (level < MAX_LEVEL) {
    const required = expRequiredForLevel(level);
    if (exp < required) break;
    exp -= required;
    level += 1;
  }

  if (level >= MAX_LEVEL) {
    level = MAX_LEVEL;
    exp = Math.min(exp, expRequiredForLevel(MAX_LEVEL) - 1);
  }

  return { level, exp };
}

export function applyRewards(state: ProgressionState, reward: EnemyReward): ProgressionState {
  const nextLevel = simulateExpGain(state.level, state.exp, reward.exp);
  return {
    level: nextLevel.level,
    exp: nextLevel.exp,
    bie: Math.max(0, Math.trunc(state.bie)) + Math.max(0, Math.trunc(reward.bie)),
    spiritEssence: Math.max(0, Math.trunc(state.spiritEssence)) + Math.max(0, Math.trunc(reward.spirit)),
  };
}

export function addHaunting(current: number, amount: number): number {
  return clampInt(current + amount, 0, 100);
}

export function shouldSpawnBoss(haunting: number, bossActive: boolean): boolean {
  return haunting >= 100 && !bossActive;
}

export function loopPhase(state: HauntingLoopState): 'hunting' | 'boss' | 'cleansing' {
  if (state.cleansingUntilMs !== null) return 'cleansing';
  if (state.bossActive) return 'boss';
  return 'hunting';
}

export class LocalProgressionLoop {
  private progressionState: ProgressionState;
  private worldState: HauntingLoopState;

  constructor(seed?: {
    progression?: Partial<ProgressionState>;
    world?: Partial<HauntingLoopState>;
  }) {
    this.progressionState = {
      ...INITIAL_PROGRESSION,
      ...seed?.progression,
    };
    this.progressionState.level = clampInt(this.progressionState.level, 1, MAX_LEVEL);
    this.progressionState.exp = Math.max(0, Math.trunc(this.progressionState.exp));
    this.progressionState.bie = Math.max(0, Math.trunc(this.progressionState.bie));
    this.progressionState.spiritEssence = Math.max(0, Math.trunc(this.progressionState.spiritEssence));

    this.worldState = {
      ...INITIAL_HAUNTING_LOOP,
      ...seed?.world,
    };
    this.worldState.haunting = clampInt(this.worldState.haunting, 0, 100);
    this.worldState.bossActive = Boolean(this.worldState.bossActive);
    this.worldState.cleansingUntilMs = this.worldState.cleansingUntilMs === null
      ? null
      : Math.max(0, Math.trunc(this.worldState.cleansingUntilMs));
    this.worldState.cycle = Math.max(0, Math.trunc(this.worldState.cycle));
  }

  get progression(): ProgressionState {
    return cloneProgression(this.progressionState);
  }

  get world(): HauntingLoopState {
    return cloneWorld(this.worldState);
  }

  defeatEnemy(enemyId: EnemyId, nowMs: number): DefeatOutcome {
    const reward = ENEMY_REWARDS[enemyId];
    const isBoss = enemyId === 'mae-tanee';
    const phase = loopPhase(this.worldState);

    if (isBoss) {
      if (!this.worldState.bossActive || phase !== 'boss') {
        return this.outcome(false, reward, false, false);
      }
      this.progressionState = applyRewards(this.progressionState, reward);
      this.worldState.bossActive = false;
      this.worldState.cleansingUntilMs = Math.max(0, Math.trunc(nowMs)) + CLEANSING_DELAY_MS;
      return this.outcome(true, reward, false, true);
    }

    if (phase !== 'hunting') {
      return this.outcome(false, reward, false, false);
    }

    this.progressionState = applyRewards(this.progressionState, reward);
    this.worldState.haunting = addHaunting(this.worldState.haunting, reward.haunting);
    const bossSpawned = shouldSpawnBoss(this.worldState.haunting, this.worldState.bossActive);
    if (bossSpawned) this.worldState.bossActive = true;
    return this.outcome(true, reward, bossSpawned, false);
  }

  tick(nowMs: number): TickOutcome {
    const cleansingUntil = this.worldState.cleansingUntilMs;
    if (cleansingUntil === null || nowMs < cleansingUntil) {
      return {
        cleansingCompleted: false,
        progression: this.progression,
        world: this.world,
      };
    }

    this.worldState.haunting = 0;
    this.worldState.bossActive = false;
    this.worldState.cleansingUntilMs = null;
    this.worldState.cycle += 1;
    return {
      cleansingCompleted: true,
      progression: this.progression,
      world: this.world,
    };
  }

  snapshot(): { progression: ProgressionState; world: HauntingLoopState } {
    return { progression: this.progression, world: this.world };
  }

  replaceCurrencies(bie: number, spiritEssence: number): ProgressionState {
    this.progressionState = {
      ...this.progressionState,
      bie: Math.max(0, Math.trunc(bie)),
      spiritEssence: Math.max(0, Math.trunc(spiritEssence)),
    };
    return this.progression;
  }

  private outcome(
    accepted: boolean,
    reward: EnemyReward,
    bossSpawned: boolean,
    bossDefeated: boolean,
  ): DefeatOutcome {
    return {
      accepted,
      reward,
      bossSpawned,
      bossDefeated,
      progression: this.progression,
      world: this.world,
    };
  }
}
